import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  FaMapMarkerAlt, 
  FaBriefcase, 
  FaClock,
  FaArrowRight,
  FaUsers,
  FaRocket,
  FaHeart,
  FaGraduationCap
} from 'react-icons/fa';
import { HiSparkles, HiLightningBolt, HiCube } from 'react-icons/hi';
import './Careers.css';

const Careers = () => {
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get('/api/jobs');
      setJobs(response.data.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setJobs(defaultJobs);
    }
  };

  const defaultJobs = [
    {
      _id: '1',
      title: 'Senior Full Stack Developer',
      department: 'Engineering',
      location: 'Mumbai, India',
      type: 'Full-time',
      description: 'Join our core engineering team to build scalable applications using modern technologies.',
      requirements: ['5+ years experience', 'React, Node.js', 'Cloud platforms']
    },
    {
      _id: '2',
      title: 'UI/UX Designer',
      department: 'Design',
      location: 'Remote',
      type: 'Full-time',
      description: 'Create stunning user experiences for our enterprise and consumer products.',
      requirements: ['3+ years experience', 'Figma expert', 'Design systems']
    },
    {
      _id: '3',
      title: 'DevOps Engineer',
      department: 'Infrastructure',
      location: 'Pune, India',
      type: 'Full-time',
      description: 'Build and maintain our cloud infrastructure and CI/CD pipelines.',
      requirements: ['4+ years experience', 'AWS/Azure', 'Kubernetes']
    },
    {
      _id: '4',
      title: 'Product Manager',
      department: 'Product',
      location: 'Bangalore, India',
      type: 'Full-time',
      description: 'Drive product strategy and work with cross-functional teams to deliver impact.',
      requirements: ['5+ years experience', 'Tech background', 'B2B experience']
    }
  ];

  const benefits = [
    {
      icon: <FaUsers />,
      title: 'Collaborative Culture',
      description: 'Work with brilliant minds in a supportive, inclusive environment.'
    },
    {
      icon: <FaRocket />,
      title: 'Growth Opportunities',
      description: 'Accelerate your career with mentorship and challenging projects.'
    },
    {
      icon: <FaHeart />,
      title: 'Work-Life Balance',
      description: 'Flexible hours, remote options, and generous time off.'
    },
    {
      icon: <FaGraduationCap />,
      title: 'Learning Budget',
      description: 'Annual budget for courses, conferences, and certifications.'
    }
  ];

  const displayJobs = jobs.length > 0 ? jobs : defaultJobs;

  return (
    <div className="careers-page">
      <Navbar />

      {/* Hero Section */}
      <section className="careers-hero">
        <div className="hero-bg">
          <div className="hero-gradient-orb orb-1"></div>
          <div className="hero-gradient-orb orb-2"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <div className="section-badge">
              <HiSparkles />
              Join Our Team
            </div>
            <h1 className="hero-title">
              Build the Future <span className="gradient-text">With Us</span>
            </h1>
            <p className="hero-description">
              Join a team of passionate innovators creating technology that makes a difference. 
              Your next career adventure starts here.
            </p>
            <a href="#positions" className="btn btn-primary btn-glow">
              View Open Positions
              <FaArrowRight />
            </a>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section benefits-section section-dark">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">
              <HiCube />
              Why Join Us
            </div>
            <h2 className="section-title">
              Perks & <span className="gradient-text">Benefits</span>
            </h2>
            <p className="section-description">
              We invest in our people because great work comes from great environments.
            </p>
          </div>

          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div 
                key={index} 
                className="benefit-card"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="benefit-icon">
                  {benefit.icon}
                </div>
                <h3 className="benefit-title">{benefit.title}</h3>
                <p className="benefit-description">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Jobs Section */}
      <section id="positions" className="section jobs-section">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">
              <HiLightningBolt />
              Open Positions
            </div>
            <h2 className="section-title">
              Find Your <span className="gradient-text">Perfect Role</span>
            </h2>
            <p className="section-description">
              We're always looking for talented individuals to join our growing team.
            </p>
          </div>

          <div className="jobs-list">
            {displayJobs.map((job, index) => (
              <div 
                key={job._id} 
                className="job-card"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="job-card-inner">
                  <div className="job-header">
                    <div className="job-info">
                      <h3 className="job-title">{job.title}</h3>
                      <p className="job-department">{job.department}</p>
                    </div>
                    <Link to="/contact" className="btn btn-primary btn-sm">
                      Apply Now
                      <FaArrowRight />
                    </Link>
                  </div>
                  
                  <p className="job-description">{job.description}</p>
                  
                  <div className="job-meta">
                    <span className="meta-item">
                      <FaMapMarkerAlt />
                      {job.location}
                    </span>
                    <span className="meta-item">
                      <FaBriefcase />
                      {job.type}
                    </span>
                    <span className="meta-item">
                      <FaClock />
                      Posted recently
                    </span>
                  </div>

                  {job.requirements && job.requirements.length > 0 && (
                    <div className="job-requirements">
                      {job.requirements.map((req, idx) => (
                        <span key={idx} className="requirement-tag">{req}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="card-border-glow"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="cta-bg">
          <div className="cta-gradient"></div>
        </div>
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">
              Don't See Your <span className="gradient-text">Dream Role</span>?
            </h2>
            <p className="cta-description">
              We're always interested in hearing from talented individuals. 
              Send us your resume and let's start a conversation.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary btn-glow">
                Get in Touch
                <FaArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Careers;
